// Per-event pricing footer for the Order breakdown table: renders the linked
// Spine order's items total, the delivery fee, a Surcharge block, a Discount
// block, then Subtotal / 9% GST / Total.
// Purely presentational — every figure comes straight from `event.breakdown`
// (Events::Breakdown), the same source the quotation PDF reads. The GST-exempt
// BCRS deposit renders after GST and is added to the displayed Total.
//
// Surcharge and Discount are a bold category label (no amount — the per-line
// amounts already roll into the subtotal, so a category total would read as a
// double count) followed by one row per fee/discount: a 12px name with its own
// amount at the menu-item value size.

import { cn } from '@/lib/utils';
import { useTranslation } from '@hooks/useTranslation';
import { formatCurrency } from '@/react/utils/formatters';
import { MenuItemNotes, toNoteLines } from './MenuItemNotes';
import type { ApiEventBreakdown } from '@api/events';

// Charge/total labels align with the dish names above (past the number gutter:
// size-6 + gap-4 = pl-10).
const LABEL_INDENT = 'pl-10';

export function EventBreakdownSummary({
  breakdown,
  bcrsDeposit = 0,
}: {
  breakdown: ApiEventBreakdown;
  bcrsDeposit?: number;
}) {
  const { t } = useTranslation();
  const label = (key: string) => t(`events_section.order_breakdown.${key}`);
  // Mirror the quotation guard: the charge block appears only when there is
  // something to charge. Subtotal / GST / Total always render.
  const hasCharges =
    breakdown.spine_items > 0 ||
    breakdown.delivery > 0 ||
    breakdown.fees.length > 0 ||
    breakdown.discounts.length > 0;
  const hasDeposit = bcrsDeposit > 0;
  const total = formatCurrency(breakdown.total + bcrsDeposit);

  return (
    <>
      {/* Desktop: a grid footer sharing the menu-items column template so the
          amount column lines up with the line-item amounts above. Text size is
          inherited from the menu-items table so the labels match its rows. */}
      <div className="hidden md:block">
        {hasCharges && (
          <>
            <Divider className="mt-8" />
            <div className="grid grid-cols-menu-items items-start gap-x-6 gap-y-3 py-8" data-testid="event-breakdown-charges">
              {breakdown.spine_items > 0 && (
                <ChargeLine label={label('spine_items')} amount={formatCurrency(breakdown.spine_items)} bold />
              )}
              {breakdown.delivery > 0 && (
                <ChargeLine label={label('delivery_fee')} amount={formatCurrency(breakdown.delivery)} bold />
              )}
              {breakdown.fees.length > 0 && (
                <>
                  <ChargeLine label={label('surcharge')} bold />
                  {breakdown.fees.map((fee, index) => (
                    <ChargeLine key={`fee-${index}`} label={fee.name} amount={formatCurrency(fee.amount)} note={fee.note} small />
                  ))}
                </>
              )}
              {breakdown.discounts.length > 0 && (
                // Discounts subtract from the subtotal, so render amounts negative
                // (matches quotation.rb's `money(-discount.amount)`).
                <>
                  <ChargeLine label={label('discount')} bold />
                  {breakdown.discounts.map((discount, index) => (
                    <ChargeLine key={`discount-${index}`} label={discount.name} amount={formatCurrency(discount.amount ? -discount.amount : 0)} note={discount.note} small />
                  ))}
                </>
              )}
            </div>
          </>
        )}
        {/* Full-width rule separates the charges from the totals block; when
            there are no charges it separates the menu items from the footer, so
            it carries the 24px top gap. */}
        <Divider className={hasCharges ? undefined : 'mt-8'} />
        <div className="grid grid-cols-menu-items gap-x-6 gap-y-4 pt-8">
          <TotalLine label={label('subtotal')} amount={formatCurrency(breakdown.subtotal)} />
          <TotalLine label={label('gst')} amount={formatCurrency(breakdown.gst)} />
          {hasDeposit && <TotalLine label={label('bcrs_deposit')} amount={formatCurrency(bcrsDeposit)} />}
          {/* Rule above Total spans the totals columns only (quantity → amount),
              not the full table width. */}
          <div aria-hidden="true" className="col-start-3 col-span-3 border-t border-grey-light" />
          <TotalLine label={label('total')} amount={total} />
        </div>
      </div>

      {/* Mobile: the same rows stacked as label → amount pairs (data parity). */}
      <div className="flex flex-col gap-3 md:hidden" data-testid="event-breakdown-mobile">
        {hasCharges && (
          <>
            <Divider />
            {breakdown.spine_items > 0 && (
              <StackLine label={label('spine_items')} amount={formatCurrency(breakdown.spine_items)} bold />
            )}
            {breakdown.delivery > 0 && (
              <StackLine label={label('delivery_fee')} amount={formatCurrency(breakdown.delivery)} bold />
            )}
            {breakdown.fees.length > 0 && (
              <>
                <StackLine label={label('surcharge')} bold />
                {breakdown.fees.map((fee, index) => (
                  <StackLine key={`fee-${index}`} label={fee.name} amount={formatCurrency(fee.amount)} note={fee.note} small />
                ))}
              </>
            )}
            {breakdown.discounts.length > 0 && (
              <>
                <StackLine label={label('discount')} bold />
                {breakdown.discounts.map((discount, index) => (
                  <StackLine key={`discount-${index}`} label={discount.name} amount={formatCurrency(discount.amount ? -discount.amount : 0)} note={discount.note} small />
                ))}
              </>
            )}
          </>
        )}
        <Divider />
        <StackLine label={label('subtotal')} amount={formatCurrency(breakdown.subtotal)} bold />
        <StackLine label={label('gst')} amount={formatCurrency(breakdown.gst)} bold />
        {hasDeposit && <StackLine label={label('bcrs_deposit')} amount={formatCurrency(bcrsDeposit)} bold />}
        <Divider />
        <StackLine label={label('total')} amount={total} bold />
      </div>
    </>
  );
}

function Divider({ className }: { className?: string }) {
  return <div aria-hidden="true" className={cn('border-t border-grey-light', className)} />;
}

function ChargeLine({
  label,
  amount,
  note,
  bold,
  small,
}: {
  label: string;
  amount?: string;
  note?: string | null;
  bold?: boolean;
  small?: boolean;
}) {
  return (
    <div className="contents">
      <div className={cn(LABEL_INDENT, bold ? 'font-semibold' : 'font-normal', small && 'text-xs')}>
        {label}
      </div>
      <div data-column="notes" className="min-w-0 max-w-full">
        <MenuItemNotes externalNotes={toNoteLines(note)} />
      </div>
      <div data-column="amount" className="col-start-5 tabular-nums font-normal">
        {amount ?? null}
      </div>
    </div>
  );
}

// Desktop totals row: the label sits in the Quantity column (right of the
// charge labels), with the amount in the amount column — the unit-price column
// stays empty between them.
function TotalLine({ label, amount }: { label: string; amount: string }) {
  return (
    <div className="contents">
      {/* Long labels overflow into the column gap rather than wrap. */}
      <div className="col-start-3 col-span-2 whitespace-nowrap font-semibold">{label}</div>
      <div data-column="amount" className="col-start-5 tabular-nums font-normal">
        {amount}
      </div>
    </div>
  );
}

// Mobile row: label → amount pair with an optional note beneath the label line.
function StackLine({
  label,
  amount,
  note,
  bold,
  small,
}: {
  label: string;
  amount?: string;
  note?: string | null;
  bold?: boolean;
  small?: boolean;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <span className={cn(bold ? 'font-semibold' : 'font-normal', small && 'text-xs')}>{label}</span>
        <MenuItemNotes externalNotes={toNoteLines(note)} className="mt-1.5" />
      </div>
      {amount != null && <span className="shrink-0 whitespace-nowrap tabular-nums font-normal">{amount}</span>}
    </div>
  );
}
